import { FilterQuery } from 'src/app/models/filter-query.model';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Plantation } from 'src/app/models/crd-state.interface';

@Injectable({
  providedIn: 'root'
})
export class EventStateService {
  // ---- STATE VARIABLES ----
  // Map & Plantation Interaction
  private readonly _zoomPlantation$ = new Subject<Plantation>();
  private readonly _hoveredPlantation$ = new Subject<Plantation | null>();
  private readonly _isShowMapPlantationDetails$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _isGeometryDataLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _isPlottingGeometryData$ = new BehaviorSubject<boolean>(
    false
  );

  // Risk Data
  private readonly _isRiskLoading$ = new BehaviorSubject<boolean>(true);
  private readonly _isRiskDataByPlantationCodeLoading$ =
    new BehaviorSubject<boolean>(true);

  // Download Loaders
  private readonly _downloadPlantationInformationLoading$ =
    new BehaviorSubject<boolean>(false);
  private readonly _downloadEuIsDataLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _downloadFullRiskReportLoading$ =
    new BehaviorSubject<boolean>(false);
  private readonly _downloadRiskVerificationAssetsLoading$ =
    new BehaviorSubject<boolean>(false);
  private readonly _downloadMappingDataQualityReportLoading$ =
    new BehaviorSubject<boolean>(false);
  private readonly _downloadMultiLinkedPlotReportLoading$ =
    new BehaviorSubject<boolean>(false);
  private readonly _downloadSurveyResponsesLoading$ =
    new BehaviorSubject<boolean>(false);
  private readonly _downloadGeojsonExportLoading$ =
    new BehaviorSubject<boolean>(false);
  private readonly _riskPreviewReportLoading$ = new BehaviorSubject<boolean>(
    false
  );

  // Upload & Import
  private readonly _matchColumnsLoading$ = new BehaviorSubject<boolean>(false);
  private readonly _validationLoading$ = new BehaviorSubject<boolean>(false);
  private readonly _isUploadEditInProgress$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _closePlantationUploadModal$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _isImportInProgress$ = new BehaviorSubject<boolean>(false);
  private readonly _hasError$ = new BehaviorSubject<boolean>(false);

  // Dashboard & Table
  private readonly _isDashboardTableLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _refreshTable$ = new Subject<boolean>();
  private readonly _plantationFiltersPayload$ =
    new BehaviorSubject<FilterQuery | null>(null);
  private readonly _isTotalSummaryLoading$ = new BehaviorSubject<boolean>(true);
  private readonly _isStatisticsUpdating$ = new BehaviorSubject<boolean>(false);
  private readonly _isPlantationFilterLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _isDeletePlantationLoading$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _isBulkDeletePlantationLoading$ =
    new BehaviorSubject<boolean>(false);
  private readonly _isFetchingAllPlantations$ = new BehaviorSubject<boolean>(
    false
  );
  private readonly _bulkSearchValues$ = new Subject<string[]>();

  // Settings & System
  private readonly _isSettingsValid$ = new BehaviorSubject<boolean>(false);
  private readonly _isSettingsLoading$ = new BehaviorSubject<boolean>(true);
  // Dataset
  private readonly _datasetChanged$ = new Subject<boolean>();

  // ---- GETTERS & SETTERS ----
  // Map & Plantation Interaction

  get zoomPlantation$() {
    return this._zoomPlantation$.asObservable();
  }

  set zoomPlantation(plantation: Plantation) {
    this._zoomPlantation$.next(plantation);
  }

  get hoveredPlantation$() {
    return this._hoveredPlantation$.asObservable();
  }

  set hoveredPlantation(hoveredPlantation: Plantation | null) {
    this._hoveredPlantation$.next(hoveredPlantation);
  }

  get isShowMapPlantationDetails$() {
    return this._isShowMapPlantationDetails$.asObservable();
  }

  set isShowMapPlantationDetails(isShowMapPlantationDetails: boolean) {
    this._isShowMapPlantationDetails$.next(isShowMapPlantationDetails);
  }

  get isGeometryDataLoading$() {
    return this._isGeometryDataLoading$.asObservable();
  }

  set isGeometryDataLoading(isGeometryDataLoading: boolean) {
    this._isGeometryDataLoading$.next(isGeometryDataLoading);
  }

  get isPlottingGeometryData$() {
    return this._isPlottingGeometryData$.asObservable();
  }

  set isPlottingGeometryData(isPlottingGeometryData: boolean) {
    this._isPlottingGeometryData$.next(isPlottingGeometryData);
  }

  // Risk Data

  get isRiskLoading$() {
    return this._isRiskLoading$.asObservable();
  }

  set isRiskLoading(isRiskLoading: boolean) {
    this._isRiskLoading$.next(isRiskLoading);
  }

  get isRiskDataByPlantationCodeLoading$() {
    return this._isRiskDataByPlantationCodeLoading$.asObservable();
  }

  set isRiskDataByPlantationCodeLoading(
    isRiskDataByPlantationCodeLoading: boolean
  ) {
    this._isRiskDataByPlantationCodeLoading$.next(
      isRiskDataByPlantationCodeLoading
    );
  }

  // Download Loaders

  get downloadPlantationInformationLoading$() {
    return this._downloadPlantationInformationLoading$.asObservable();
  }

  set downloadPlantationInformationLoading(
    downloadPlantationInformationLoading: boolean
  ) {
    this._downloadPlantationInformationLoading$.next(
      downloadPlantationInformationLoading
    );
  }

  get downloadEuIsDataLoading$() {
    return this._downloadEuIsDataLoading$.asObservable();
  }

  set downloadEuIsDataLoading(downloadEuIsDataLoading: boolean) {
    this._downloadEuIsDataLoading$.next(downloadEuIsDataLoading);
  }

  get downloadFullRiskReportLoading$() {
    return this._downloadFullRiskReportLoading$.asObservable();
  }

  set downloadFullRiskReportLoading(downloadFullRiskReportLoading: boolean) {
    this._downloadFullRiskReportLoading$.next(downloadFullRiskReportLoading);
  }

  get downloadRiskVerificationAssetsLoading$() {
    return this._downloadRiskVerificationAssetsLoading$.asObservable();
  }

  set downloadRiskVerificationAssetsLoading(
    downloadRiskVerificationAssetsLoading: boolean
  ) {
    this._downloadRiskVerificationAssetsLoading$.next(
      downloadRiskVerificationAssetsLoading
    );
  }

  get downloadMappingDataQualityReportLoading$() {
    return this._downloadMappingDataQualityReportLoading$.asObservable();
  }

  set downloadMappingDataQualityReportLoading(
    downloadMappingDataQualityReportLoading: boolean
  ) {
    this._downloadMappingDataQualityReportLoading$.next(
      downloadMappingDataQualityReportLoading
    );
  }

  get downloadMultiLinkedPlotReportLoading$() {
    return this._downloadMultiLinkedPlotReportLoading$.asObservable();
  }

  set downloadMultiLinkedPlotReportLoading(
    downloadMultiLinkedPlotReportLoading: boolean
  ) {
    this._downloadMultiLinkedPlotReportLoading$.next(
      downloadMultiLinkedPlotReportLoading
    );
  }

  get downloadSurveyResponsesLoading$() {
    return this._downloadSurveyResponsesLoading$.asObservable();
  }

  set downloadSurveyResponsesLoading(downloadSurveyResponsesLoading: boolean) {
    this._downloadSurveyResponsesLoading$.next(downloadSurveyResponsesLoading);
  }

  get downloadGeojsonExportLoading$() {
    return this._downloadGeojsonExportLoading$.asObservable();
  }

  set downloadGeojsonExportLoading(downloadGeojsonExportLoading: boolean) {
    this._downloadGeojsonExportLoading$.next(downloadGeojsonExportLoading);
  }

  get riskPreviewReportLoading$() {
    return this._riskPreviewReportLoading$.asObservable();
  }

  set riskPreviewReportLoading(riskPreviewReportLoading: boolean) {
    this._riskPreviewReportLoading$.next(riskPreviewReportLoading);
  }

  // Upload & Import

  get matchColumnsLoading$() {
    return this._matchColumnsLoading$.asObservable();
  }

  set matchColumnsLoading(matchColumnsLoading: boolean) {
    this._matchColumnsLoading$.next(matchColumnsLoading);
  }

  get validationLoading$() {
    return this._validationLoading$.asObservable();
  }

  set validationLoading(validationLoading: boolean) {
    this._validationLoading$.next(validationLoading);
  }

  get isUploadEditInProgress$() {
    return this._isUploadEditInProgress$.asObservable();
  }

  set isUploadEditInProgress(isUploadEditInProgress: boolean) {
    this._isUploadEditInProgress$.next(isUploadEditInProgress);
  }

  get closePlantationUploadModal$(): Observable<boolean> {
    return this._closePlantationUploadModal$.asObservable();
  }

  set closePlantationUploadModal(closePlantationUploadModal: boolean) {
    this._closePlantationUploadModal$.next(closePlantationUploadModal);
  }

  get isImportInProgress$() {
    return this._isImportInProgress$.asObservable();
  }

  set isImportInProgress(isImportInProgress: boolean) {
    this._isImportInProgress$.next(isImportInProgress);
  }

  get hasError$() {
    return this._hasError$.asObservable();
  }

  set hasError(hasError: boolean) {
    this._hasError$.next(hasError);
  }

  // Dashboard & Table

  get isDashboardTableLoading$() {
    return this._isDashboardTableLoading$.asObservable();
  }

  set isDashboardTableLoading(isDashboardTableLoading: boolean) {
    this._isDashboardTableLoading$.next(isDashboardTableLoading);
  }

  get refreshTable$() {
    return this._refreshTable$.asObservable();
  }

  set refreshTable(refreshTable: boolean) {
    this._refreshTable$.next(refreshTable);
  }

  get plantationFiltersPayload$() {
    return this._plantationFiltersPayload$.asObservable();
  }

  set plantationFiltersPayload(filters: FilterQuery | null) {
    this._plantationFiltersPayload$.next(filters);
  }

  get isTotalSummaryLoading$() {
    return this._isTotalSummaryLoading$.asObservable();
  }

  set isTotalSummaryLoading(isTotalSummaryLoading: boolean) {
    this._isTotalSummaryLoading$.next(isTotalSummaryLoading);
  }

  get isStatisticsUpdating$() {
    return this._isStatisticsUpdating$.asObservable();
  }

  set isStatisticsUpdating(isStatisticsUpdating: boolean) {
    this._isStatisticsUpdating$.next(isStatisticsUpdating);
  }

  get isPlantationFilterLoading$() {
    return this._isPlantationFilterLoading$.asObservable();
  }

  set isPlantationFilterLoading(isPlantationFilterLoading: boolean) {
    this._isPlantationFilterLoading$.next(isPlantationFilterLoading);
  }

  get isDeletePlantationLoading$() {
    return this._isDeletePlantationLoading$.asObservable();
  }

  set isDeletePlantationLoading(isDeletePlantationLoading: boolean) {
    this._isDeletePlantationLoading$.next(isDeletePlantationLoading);
  }

  get isBulkDeletePlantationLoading$() {
    return this._isBulkDeletePlantationLoading$.asObservable();
  }

  set isBulkDeletePlantationLoading(isBulkDeletePlantationLoading: boolean) {
    this._isBulkDeletePlantationLoading$.next(isBulkDeletePlantationLoading);
  }

  get isFetchingAllPlantations$() {
    return this._isFetchingAllPlantations$.asObservable();
  }

  set isFetchingAllPlantations(isFetchingAllPlantations: boolean) {
    this._isFetchingAllPlantations$.next(isFetchingAllPlantations);
  }

  set bulkSearchValues(value: string[]) {
    this._bulkSearchValues$.next(value);
  }

  get bulkSearchValues$() {
    return this._bulkSearchValues$.asObservable();
  }

  // Settings & System

  get isSettingsValid$() {
    return this._isSettingsValid$.asObservable();
  }

  set isSettingsValid(isSettingsValid: boolean) {
    this._isSettingsValid$.next(isSettingsValid);
  }

  get isSettingsLoading$() {
    return this._isSettingsLoading$.asObservable();
  }

  set isSettingsLoading(isSettingsLoading: boolean) {
    this._isSettingsLoading$.next(isSettingsLoading);
  }

  // --- Dataset ---

  get datasetChanged$() {
    return this._datasetChanged$.asObservable();
  }

  set datasetChanged(datasetChanged: boolean) {
    this._datasetChanged$.next(datasetChanged);
  }

  // Reset

  resetAllLoadingStates() {
    this.isRiskLoading = false;
    this.downloadEuIsDataLoading = false;
    this.downloadPlantationInformationLoading = false;
    this.downloadMappingDataQualityReportLoading = false;
    this.validationLoading = false;
    this.downloadFullRiskReportLoading = false;
    this.downloadRiskVerificationAssetsLoading = false;
    this.downloadMultiLinkedPlotReportLoading = false;
    this.downloadSurveyResponsesLoading = false;
    this.downloadGeojsonExportLoading = false;
    this.riskPreviewReportLoading = false;
    this.isDeletePlantationLoading = false;
    this.isDashboardTableLoading = false;
    this.isTotalSummaryLoading = false;
    this.isStatisticsUpdating = false;
    this.isGeometryDataLoading = false;
    this.isUploadEditInProgress = false;
    this.isImportInProgress = false;
    this.validationLoading = false;
    this.isSettingsLoading = false;
    this.isRiskDataByPlantationCodeLoading = false;
  }
}
